import React, { useState, useEffect, useCallback, useMemo, type Ref, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Box, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import AllShopsSelector from '@/components/selectors/AllShopsSelector';
import DateRangeSelector from '@/components/selectors/DateRangeSelector';
import { useSettingsStore } from '@core/store';
import { usePersistence } from '@/components/shared/navigation/securePage';
import { FilterRef } from '@features/statistics/types.ts';

export interface FilterShopAllDateRangeProps {
    allShopsSelectedOnStart?: boolean;
    showDateRangeInfo?: boolean;
    range?: number;
    maxRange?: number;
    onLoadData: (data: FilterShopAllDateRangeData) => void;
    ref?: Ref<FilterRef>;
}

export interface FilterShopAllDateRangeData {
    shopIds: string[];
    start: string;
    end: string;
}

const FilterShopAllDateRange = ({
    allShopsSelectedOnStart = false,
    showDateRangeInfo = false,
    range = 7,
    maxRange = 30,
    onLoadData,
    ref
}: FilterShopAllDateRangeProps) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const { darkMode } = useSettingsStore();

    const [startDate, setStartDate] = useState<DateTime>(DateTime.local().minus({ days: range }));
    const [endDate, setEndDate] = useState<DateTime>(DateTime.local());

    const {
        shop: { selectedShopIds, setSelectedShopIds }
    } = usePersistence({ shop: { preselect: allShopsSelectedOnStart ? 'all' : 'first', inheritsPersisted: true } });

    // Validation
    const errorMessages = useMemo(() => {
        const daysDiff = Math.abs(endDate.diff(startDate, 'days').days);
        return daysDiff <= maxRange
            ? []
            : [t('errors.rangeLtDays', { days: maxRange }) || `Range must be less than ${maxRange} days`];
    }, [startDate, endDate, maxRange, t]);

    const emitLoadData = useCallback(() => {
        const start = startDate.startOf('day').toISO();
        const end = endDate.endOf('day').toISO();

        if (start && end) {
            onLoadData({
                shopIds: selectedShopIds,
                start,
                end
            });
        }
    }, [startDate, endDate, selectedShopIds, onLoadData]);

    useImperativeHandle(ref, () => ({ refresh: () => emitLoadData() }), [emitLoadData]);

    useEffect(() => {
        if (selectedShopIds.length === 0 || errorMessages.length > 0) return;
        emitLoadData();
    }, [selectedShopIds.join(','), startDate, endDate]);

    // Handle route query parameters
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const shopIdsParam = params.get('shopIds');
        const startParam = params.get('start');
        const endParam = params.get('end');

        if (shopIdsParam) {
            setSelectedShopIds(shopIdsParam.split(','));
        }

        if (startParam) {
            const parsedStartDate = DateTime.fromISO(startParam, { zone: 'UTC' });
            if (parsedStartDate.isValid) {
                setStartDate(parsedStartDate);
            }
        }

        if (endParam) {
            const parsedEndDate = DateTime.fromISO(endParam, { zone: 'UTC' });
            if (parsedEndDate.isValid) {
                setEndDate(parsedEndDate);
            }
        }
    }, [location.search]);

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
            <Box
                className="filter-row"
                sx={{
                    display: 'flex',
                    gap: 2.9,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    mb: 5,
                    mt: 1
                }}
            >
                {/* Shop Selector */}
                <Box className="filter-field" sx={{ flex: '0 0 auto' }}>
                    <AllShopsSelector value={selectedShopIds} onChange={setSelectedShopIds} label={t('shop')} />
                </Box>

                {/* Date Range Selector */}
                <DateRangeSelector
                    startDate={startDate.toISODate() || ''}
                    endDate={endDate.toISODate() || ''}
                    onStartDateChange={(date: string) => setStartDate(DateTime.fromISO(date, { zone: 'UTC' }))}
                    onEndDateChange={(date: string) => setEndDate(DateTime.fromISO(date, { zone: 'UTC' }))}
                    maxRange={maxRange}
                    locale={i18n.language}
                    error={errorMessages.length > 0}
                    helperText={errorMessages.length > 0 ? errorMessages[0] : undefined}
                />

                {/* Flexible spacer that optionally shows date range info */}
                <Box sx={{ flex: '1 1 auto', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
                    {showDateRangeInfo && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary',
                                fontStyle: 'italic'
                            }}
                        >
                            {startDate &&
                                endDate &&
                                `${Math.abs(endDate.diff(startDate, 'days').days)} ${t('days')} ${t('selected')}`}
                        </Typography>
                    )}
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default FilterShopAllDateRange;
